import { CheckCircle2, Copy, MoreHorizontal, Pencil, Trash2, Undo2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { Occurrence } from "@/lib/scheduled";
import { formatDate, money } from "@/lib/format";

export type Filter = "all" | "income" | "expense";
const FILTERS: [Filter, string][] = [["all", "Todo"], ["income", "Ingresos"], ["expense", "Gastos"]];

export function FilterTabs({ value, onChange }: { value: Filter; onChange: (f: Filter) => void }) {
  return (
    <div className="flex gap-1 rounded-lg bg-[#F4F5F7] p-1" data-testid="scheduled-filter">
      {FILTERS.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`scheduled-filter-${v}`}
          className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${value === v ? "bg-white text-[#1B2A4A] shadow-sm" : "text-slate-500 hover:text-[#1B2A4A]"}`}>{l}</button>
      ))}
    </div>
  );
}

export interface RowHandlers { onPay: (o: Occurrence) => void; onEdit: (o: Occurrence) => void; onDelete: (o: Occurrence) => void; onDuplicate: (o: Occurrence) => void; onUndo: (o: Occurrence) => void; paying: string | null }
interface RowProps extends RowHandlers { o: Occurrence; idx: number }

function Actions({ o, idx, onPay, onEdit, onDelete, onDuplicate, onUndo, paying, where }: RowProps & { where: "m" | "d" }) {
  const inflow = o.type === "income";
  if (o.paid) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger className="flex h-9 w-9 items-center justify-center rounded-md border border-[#DDE1E8] text-slate-400 hover:bg-[#F4F5F7] sm:h-8 sm:w-8 sm:border-0" aria-label="Acciones" data-testid={`scheduled-actions-${where}-${o.schedule_id}-${idx}`}>
          <MoreHorizontal className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem onClick={() => onUndo(o)} data-testid={`scheduled-undo-${where}-${o.transaction_id}`}><Undo2 className="mr-2 h-4 w-4" /> {inflow ? "Deshacer cobro" : "Deshacer pago"}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex h-9 w-9 items-center justify-center rounded-md border border-[#DDE1E8] text-slate-500 hover:bg-[#F4F5F7] sm:h-8 sm:w-8 sm:border-0" aria-label="Acciones" data-testid={`scheduled-actions-${where}-${o.schedule_id}-${idx}`}>
        <MoreHorizontal className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem disabled={paying === o.schedule_id} onClick={() => onPay(o)} data-testid={`scheduled-pay-${where}-${o.schedule_id}-${idx}`}><CheckCircle2 className="mr-2 h-4 w-4" /> {o.is_oldest ? (inflow ? "Marcar como cobrado" : "Marcar como pagado") : (inflow ? "Cobrar por adelantado" : "Pagar por adelantado")}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(o)} data-testid={`scheduled-edit-${o.schedule_id}-${idx}`}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDuplicate(o)} data-testid={`scheduled-duplicate-${o.schedule_id}-${idx}`}><Copy className="mr-2 h-4 w-4" /> Duplicar</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDelete(o)} className="text-[#B23A1E]" data-testid={`scheduled-delete-${o.schedule_id}-${idx}`}><Trash2 className="mr-2 h-4 w-4" /> Eliminar</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Row(p: RowProps) {
  const { o, idx, onPay, paying } = p;
  const inflow = o.type === "income";
  const tone = o.paid ? "text-slate-400" : "";
  return (
    <li className={`grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1 py-4 sm:grid-cols-[70px_minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(0,1.2fr)_120px_190px_36px] ${o.paid ? "opacity-70" : ""}`}
      data-testid={`occurrence-${o.schedule_id}-${idx}`}>
      <span className={`text-sm font-semibold ${o.overdue ? "text-[#B23A1E]" : o.paid ? "text-slate-400" : "text-slate-600"}`}>{formatDate(o.date).slice(0, 5)}{o.overdue && <span className="block text-[11px] font-bold uppercase">Vencido</span>}</span>
      <span className={`min-w-0 break-words font-semibold leading-snug sm:order-none ${o.paid ? "text-slate-400" : "text-[#1B2A4A]"}`}>{o.description}</span>
      <span className={`tabular text-right font-semibold sm:order-5 ${o.paid ? "text-slate-400" : inflow ? "text-[#1B6E4F]" : "text-[#1B2A4A]"}`} data-testid={`occurrence-amount-${o.schedule_id}-${idx}`}>{inflow ? "+" : "−"}{money(o.amount)}</span>
      <span className={`col-start-2 text-xs sm:col-start-auto sm:order-3 sm:text-sm ${tone || "text-slate-500"}`} data-testid={`occurrence-label-${o.schedule_id}-${idx}`}>
        {o.paid ? `${o.label} ✓` : o.label}{o.changed && !o.paid ? " · solo este pago cambiado" : ""}
      </span>
      <span className={`col-start-2 min-w-0 break-words text-xs sm:col-start-auto sm:order-4 sm:text-sm ${tone || "text-slate-500"}`}>{o.account_name}</span>
      <div className="col-span-2 col-start-2 flex items-center justify-between gap-2 sm:order-6 sm:col-span-1 sm:col-start-auto">
        {o.is_oldest && !o.paid ? (
          <button onClick={() => onPay(o)} disabled={paying === o.schedule_id} data-testid={`pay-${o.schedule_id}`}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-[#1B2A4A] px-3 py-1.5 text-xs font-semibold text-[#1B2A4A] transition-colors hover:bg-[#1B2A4A] hover:text-white disabled:opacity-50">
            <CheckCircle2 className="h-3.5 w-3.5" /> {inflow ? "Marcar como cobrado" : "Marcar como pagado"}
          </button>
        ) : <span />}
        {o.transaction_id || !o.paid ? <span className="sm:hidden"><Actions {...p} where="m" /></span> : null}
      </div>
      <span className="hidden sm:order-7 sm:block"><Actions {...p} where="d" /></span>
    </li>
  );
}

export function ScheduledList({ items, ...handlers }: { items: Occurrence[] } & RowHandlers) {
  if (items.length === 0) return <p className="py-10 text-center text-sm text-slate-500" data-testid="scheduled-empty">No hay nada programado en este mes.</p>;
  return (
    <ul className="divide-y divide-[#DDE1E8]" data-testid="scheduled-list">
      {items.map((o, i) => <Row key={`${o.schedule_id}-${o.transaction_id ?? o.occurrence_date}-${o.date}`} o={o} idx={i} {...handlers} />)}
    </ul>
  );
}
